import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  bufferToggle,
  catchError,
  concatMap,
  debounceTime,
  filter,
  flatMap,
  map,
  tap,
  windowToggle,
} from 'rxjs/operators';

import { KPI } from '../api';
import { AssetsService } from './assets.service';
import { ErrorService } from './error.service';
import { NotationsService } from './notations.service';

@Injectable()
export class AssetsChangesService {

  public pendingChangesNumber$ = new ReplaySubject<number>(1);
  public autoSave$ = new BehaviorSubject<boolean>(true);

  private change$: Subject<Partial<KPI.Asset>>;
  private save$ = new Subject<void>();
  private pendingChangesNumber: number = 0;

  constructor(
    private errorService: ErrorService,
    private notationsService: NotationsService,
    private assetsService: AssetsService,
  ) {
    this.initChanges();

    // When all changes have been saved, reload the technical state notations
    this.pendingChangesNumber$
      .pipe(debounceTime(100))
      .subscribe((pendingChangesNumber) => {
        if (pendingChangesNumber === 0) {
          this.notationsService.refreshNotations();
        }
      })
      ;
  }

  public addChanges(assetId: number, changes: {property: string, value: any}[]): void {
    const change: Partial<KPI.Initiative> = { id: assetId };
    changes.forEach((c) => change[c.property] = c.value);

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public addChange(assetId: number, property: string, value: any): void {
    const change: Partial<KPI.Asset> = { id: assetId };
    change[property] = value;

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public save(): void {
    this.save$.next();
    this.autoSave$.next(this.autoSave$.value);
  }

  private initChanges(): void {
    this.change$ = new Subject<Partial<KPI.Asset>>();
    this.saveChanges();
    this.pendingChangesNumber = 0;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
  }

  private autoSaveOn$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === true));
  }

  private autoSaveOff$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === false));
  }

  private saveChanges(): Subscription {
    return merge(
      this.change$.pipe(bufferToggle(this.autoSaveOff$(), () => merge(this.autoSaveOn$(), this.save$))),
      this.change$.pipe(windowToggle(this.autoSaveOn$(), () => this.autoSaveOff$())),
    ).pipe(
      flatMap((assets) => assets),
      concatMap((asset) =>
        this.assetsService.updateAsset(
          asset.id,
          asset,
        ).pipe(
          catchError((error) => {
            this.errorService.handleError(error, true);
            return of(null);
          }),
          map((r) => r),
        ),
      ),
      tap(() => this.updatePendingChanges(-1)),
    ).subscribe(
      () => { },
    );
  }

  private updatePendingChanges(increment: number): void {
    this.pendingChangesNumber += increment;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
  }
}
